import { PipeTransform, Pipe } from '@angular/core';
import { DatePipe } from '@angular/common';
import { XsI18nPipe } from '@nx-fe/pipes';
import { TranslateService } from '@ngx-translate/core';

@Pipe({
  name: 'dateFormatPipe',
})
export class ShortDateFormatPipe implements PipeTransform {
  constructor(private saI18nPipe: XsI18nPipe, private datePipe: DatePipe, private translate: TranslateService) {}

  private static isYesterDay = (now: Date, inputDate: Date): boolean => {
    const yesterDay: Date = new Date(now.getTime());
    yesterDay.setDate(now.getDate() - 1);
    return (
      inputDate.getDate() === yesterDay.getDate() &&
      inputDate.getMonth() === yesterDay.getMonth() &&
      inputDate.getFullYear() === yesterDay.getFullYear()
    );
  };

  private static isTomorrow = (now: Date, inputDate: Date): boolean => {
    const tomorrow: Date = new Date(now.getTime());
    tomorrow.setDate(now.getDate() + 1);
    return (
      inputDate.getDate() === tomorrow.getDate() &&
      inputDate.getMonth() === tomorrow.getMonth() &&
      inputDate.getFullYear() === tomorrow.getFullYear()
    );
  };

  private static isYearOld = (now: Date, inputDate: Date): boolean => {
    const lastYear = new Date(now.getTime());
    lastYear.setFullYear(now.getFullYear() - 1);
    return inputDate <= lastYear;
  };

  transform(date: string, shortForm?: boolean, dueDate?: boolean, timezone?: string): string {
    const now = new Date();
    const inputDate = new Date(date);

    // Get difference in seconds
    const diff = Math.abs((now.getTime() - inputDate.getTime()) / 1000);

    // get the seconds of one day
    const secondsInDay = 60 * 60 * 24;

    // Is the date in the future?
    const future = inputDate > now;

    let caption: string;
    const local = this.translate.currentLang;

    if (diff < 60) {
      // Less than a minute difference

      return this.saI18nPipe.transform('timeFormatFilter_Now');
    } else if (
      diff <= 60 * 60 ||
      (now.getDate() === inputDate.getDate() &&
        now.getMonth() === inputDate.getMonth() &&
        now.getFullYear() === inputDate.getFullYear())
    ) {
      // Same day
      const diffInHours = Math.floor(diff / 3600);
      const minutesRemainder = Math.floor(diff / 60) - diffInHours * 60;
      let formatedHour: string;
      let formatedMins: string;
      let formatedHoursMinutes: string;
      let timeFormatCapture: string;

      if (shortForm) {
        caption = future ? 'timeFormatFilter_HoursMinutes_Caption' : 'timeFormatFilter_HoursMinutesAgo_Caption';
        formatedHour = this.saI18nPipe.transform('timeFormatFilter_FormatedHours', diffInHours);
        formatedMins =
          minutesRemainder > 0 ? this.saI18nPipe.transform('timeFormatFilter_FormatedMinutes', minutesRemainder) : '';
      } else {
        caption = future ? 'timeFormatFilter_InHoursMinutes_Caption' : 'timeFormatFilter_HoursMinutesAgo_Caption';
        timeFormatCapture =
          diffInHours > 1 ? 'timeFormatFilter_Long_FormatedHours' : 'timeFormatFilter_Long_FormatedHour';
        formatedHour = this.saI18nPipe.transform(timeFormatCapture, diffInHours);

        timeFormatCapture =
          minutesRemainder > 1 ? 'timeFormatFilter_Long_FormatedMinutes' : 'timeFormatFilter_Long_FormatedMinute';
        formatedMins = this.saI18nPipe.transform(timeFormatCapture, minutesRemainder);
      }

      if (diffInHours === 0) {
        formatedHour = '';
      }

      if (minutesRemainder === 0) {
        formatedMins = '';
      }

      formatedHoursMinutes = this.saI18nPipe.transform(
        'timeFormatFilter_FormatedHourMinutes',
        formatedHour,
        formatedMins
      );
      return this.saI18nPipe.transform(caption, formatedHoursMinutes.trim());
    } else if (ShortDateFormatPipe.isYesterDay(now, inputDate)) {
      // if it is yesterday
      if (shortForm) {
        if (dueDate) {
          // if time format is for dueDate
          caption = 'timeFormatFilter_Due_DayAgo_Caption';
          return this.saI18nPipe.transform(caption, '1');
        }

        return this.saI18nPipe.transform('timeFormatFilter_Yesterday');
      }

      return (
        this.saI18nPipe.transform('timeFormatFilter_Long_Yesterday') +
        this.datePipe.transform(date, this.saI18nPipe.transform('timeFormatFilter_Long_Time'), timezone, local)
      );
    } else if (ShortDateFormatPipe.isTomorrow(now, inputDate)) {
      // the date is tomorrow
      if (shortForm) {
        if (dueDate) {
          return this.saI18nPipe.transform('timeFormatFilter_Due_Tomorrow');
        }
        return this.saI18nPipe.transform('timeFormatFilter_MonthDay_Format');
      }
      const diffInHours = Math.ceil(diff / 3600);
      return (
        this.saI18nPipe.transform('timeFormatFilter_Long_Tomorrow') +
        this.datePipe.transform(date, this.saI18nPipe.transform('timeFormatFilter_Long_Time'), timezone, local) +
        this.saI18nPipe.transform('timeFormatFilter_Long_InHours_Caption', diffInHours)
      );
    } else if (!shortForm) {
      let result = this.datePipe.transform(
        date,
        this.saI18nPipe.transform('timeFormatFilter_Long_MonthDayYearHourMinutes_Format'),
        timezone, local
      );
      if (diff / secondsInDay < 30) {
        if (future) {
          caption = 'timeFormatFilter_Long_InDays_Caption';
        } else {
          caption = 'timeFormatFilter_Long_DaysAgo_Caption';
        }

        result += ' ' + this.saI18nPipe.transform(caption, Math.ceil(diff / secondsInDay));
      }
      return result;
    } else if (now.getFullYear() === inputDate.getFullYear()) {
      // Same year
      return this.datePipe.transform(date, this.saI18nPipe.transform('timeFormatFilter_MonthDay_Format'), timezone, local);
    } else {
      // Different year
      if (ShortDateFormatPipe.isYearOld(now, inputDate) && !dueDate) {
        return this.datePipe.transform(date, this.saI18nPipe.transform('timeFormatFilter_Year_Format'), timezone, local);
      }
      return this.datePipe.transform(date, this.saI18nPipe.transform('timeFormatFilter_MonthDayYear_Format'), timezone, local);
    }
  }
}
